import { parse, differenceInMonths, isValid, compareAsc, isWithinInterval } from 'date-fns';
import { LoanEvent, loanStatusDescriptionRemapping } from '@/types/nsldsTypes';

interface ProcessedEventsResult {
  qualifiedPaymentMonths: number;
  processedEvents: LoanEvent[];
}

// Memoized date parsing (keep this optimization)
const parseEventDateMemo: Record<string, Date | null> = {};
function parseEventDate(dateString: string | undefined): Date | null {
  if (!dateString) return null;
  if (dateString in parseEventDateMemo) {
    return parseEventDateMemo[dateString];
  }
  const date = parse(dateString, 'MM/dd/yyyy', new Date());
  const result = isValid(date) ? date : null;
  parseEventDateMemo[dateString] = result;
  return result;
}

// Define the COVID Forbearance Window
// Note: The end date for isWithinInterval is exclusive, so we use Oct 1, 2023
const covidForbearanceStartDate = new Date(2020, 2, 13); // March is month 2 (0-indexed)
const covidForbearanceEndDateExclusive = new Date(2023, 9, 1); // October is month 9

/**
 * Processes the lines corresponding to the Loan Status / Loan Event section
 * for a single loan, calculating the total months spent in repayment.
 * Assumes event lines are sorted from most recent to least recent in the input.
 *
 * @param eventLines - The lines belonging to the event section (newest first).
 * @param loanAwardId - The Award ID of the current loan.
 * @param fileEndDate - The reference end date (from file request date or current date).
 * @param repaymentBeginDate - The repayment begin date (from file request date or current date).
 * @returns An object containing the calculated qualified payment months and the list of processed event objects.
 */
export function processLoanEvents(
  eventLines: string[],
  loanAwardId: string,
  fileEndDate: Date,
  repaymentBeginDate: Date | null
): ProcessedEventsResult {
  const rawEvents: Partial<LoanEvent & { parsedDate?: Date | null }>[] = [];
  let currentEvent: Partial<LoanEvent & { parsedDate?: Date | null }> | null = null;

  // --- Pass 1: Structure events from lines (maintains newest-to-oldest order) ---
  for (const line of eventLines) {
    const trimmedLine = line.trim();
    const separatorIndex = trimmedLine.indexOf(':');
    if (separatorIndex === -1 || !trimmedLine) continue;

    const key = trimmedLine.substring(0, separatorIndex).trim();
    const value = trimmedLine.substring(separatorIndex + 1).trim();

    if (key === 'Loan Status') {
      if (currentEvent) {
        rawEvents.push(currentEvent);
      }
      currentEvent = { 'Loan Award ID': loanAwardId, 'Loan Status': value };
    } else if (currentEvent) {
      if (key === 'Loan Status Description') {
        currentEvent['Loan Status Description'] = loanStatusDescriptionRemapping[value] || value;
      } else if (key === 'Loan Status Effective Date') {
        currentEvent['Loan Status Effective Date'] = value;
        currentEvent.parsedDate = parseEventDate(value);
      }
    }
  }
  if (currentEvent) {
    rawEvents.push(currentEvent);
  }

  // Filter out events missing essential info and keep parsedDate temporarily
  const validEvents = rawEvents.filter(
    (event): event is LoanEvent & { parsedDate: Date } =>
      !!event.parsedDate &&
      !!event['Loan Status'] &&
      !!event['Loan Status Description'] &&
      isValid(event.parsedDate)
  );

  if (validEvents.length === 0) {
    return { qualifiedPaymentMonths: 0, processedEvents: [] };
  }

  // --- Verification: Check if dates are indeed newest to oldest ---
  for (let i = 0; i < validEvents.length - 1; i++) {
    // Use compareAsc: positive means date[i] > date[i+1] (correct order)
    // negative means date[i] < date[i+1] (incorrect order - older date appears before newer date)
    if (compareAsc(validEvents[i].parsedDate, validEvents[i + 1].parsedDate) < 0) {
      console.warn(
        `Loan ${loanAwardId}: Events may not be sorted newest-to-oldest as expected. Found event ${
          validEvents[i]['Loan Status Effective Date'] ?? '[Date Missing]'
        } (parsed as ${validEvents[
          i
        ].parsedDate.toISOString()}) which is older than the next event ${
          validEvents[i + 1]['Loan Status Effective Date'] ?? '[Date Missing]'
        } (parsed as ${validEvents[
          i + 1
        ].parsedDate.toISOString()}). Calculation might be inaccurate.`
      );
      console.warn(validEvents[i]);
      console.warn(validEvents[i + 1]);
      // Depending on severity, you might choose to throw an error or force a sort here.
      // For now, we'll proceed assuming the input *should* be sorted.
      break; // Log only the first occurrence
    }
  }

  // --- Pass 2: Calculate qualified months (Iterating logically oldest to newest) ---
  // We iterate through the validEvents array backwards (from index length-1 to 0)
  // which corresponds to processing events from oldest to newest chronologically.
  let qualifiedMonths = 0;

  // --- Initial Repayment Start Date Determination ---
  let initialRepaymentStartDate: Date | null = null;
  // Use the explicit repaymentBeginDate if it exists and is valid
  if (repaymentBeginDate && isValid(repaymentBeginDate)) {
    initialRepaymentStartDate = repaymentBeginDate;
  } else if (validEvents.length > 0) {
    // Otherwise, if no valid explicit date, check the oldest event
    const oldestEvent = validEvents[validEvents.length - 1];
    if (oldestEvent['Loan Status Description'] === 'IN REPAYMENT') {
      // Use the oldest event's date ONLY if no valid explicit date was provided
      initialRepaymentStartDate = oldestEvent.parsedDate;
    }
  }

  // --- Iterate Oldest to Newest (backwards through validEvents array) ---
  let currentRepaymentStartDate: Date | null = initialRepaymentStartDate;

  // Reset qualifiedMonths before calculation loop
  qualifiedMonths = 0;

  for (let i = validEvents.length - 1; i >= 0; i--) {
    const event = validEvents[i];
    const currentEventDate = event.parsedDate;
    const statusDesc = event['Loan Status Description'];

    // --- Determine if the current status is qualifying --- //
    const isQualifyingStatus =
      statusDesc === 'IN REPAYMENT' ||
      (statusDesc === 'FORBEARANCE' &&
        isWithinInterval(currentEventDate, {
          start: covidForbearanceStartDate,
          end: covidForbearanceEndDateExclusive,
        }));

    // If we are currently tracking a repayment period
    if (currentRepaymentStartDate) {
      // Check if this event *ends* the qualifying period (i.e., status is not qualifying)
      if (!isQualifyingStatus) {
        qualifiedMonths += differenceInMonths(currentEventDate, currentRepaymentStartDate);
        currentRepaymentStartDate = null; // Stop tracking this period
      }
      // else: status is still IN REPAYMENT, continue tracking from currentRepaymentStartDate
    } else {
      // If we are *not* currently tracking a repayment period, check if this event *starts* one
      if (isQualifyingStatus) {
        currentRepaymentStartDate = currentEventDate; // Start tracking
      }
      // else: status is not qualifying, and we weren't tracking, so do nothing
    }
  }

  // After the loop, if we were still tracking a repayment period (last status was IN REPAYMENT)
  if (currentRepaymentStartDate) {
    qualifiedMonths += differenceInMonths(fileEndDate, currentRepaymentStartDate);
  }

  qualifiedMonths = Math.max(0, qualifiedMonths);

  // Prepare the final list of events without the temporary parsedDate
  // Ensure the final list retains the original (newest-to-oldest) order from the input file.
  const processedEvents: LoanEvent[] = validEvents.map(({ ...rest }) => rest);

  return {
    qualifiedPaymentMonths: qualifiedMonths,
    processedEvents, // Return the events in their original (newest-to-oldest) parsed order
  };
}
